import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { summarizeJob, getJobNotes } from "./aiService";
import "./ai.css";
import html2canvas from "html2canvas";
import jsPDF from 'jspdf';
import toast from 'react-hot-toast';

const AiChatDialog = ({ open, onClose, job, userId }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [jobNotes, setJobNotes] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const QUICK_PROMPTS = [
    "Summarize all site notes for this job",
    "Give me a quick status update",
    "What was done today?",
    "Show tasks with their current status",
    "What needs immediate attention?",
    "What should be the next steps?",
  ];
  const [showQuickPrompts, setShowQuickPrompts] = useState(false);
  const [renderQuickPrompts, setRenderQuickPrompts] = useState(false);
  const panelCloseTimer = useRef(null);
  const [lastFailedInput, setLastFailedInput] = useState(null);
  const bodyRef = useRef(null);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [maxNotes, setMaxNotes] = useState('All');

  // quick prompts are provided in `QUICK_PROMPTS` above

  useEffect(() => {
    if (!open) return;
    // initialize conversation with system prompt
    setMessages([
      {
        id: "system",
        role: "system",
        content: `Hello — how can I help you with the job "${job?.jobName || "this job"}"? You can ask me to summarize notes, list tasks, show attachments, or extract deadlines.`,
      },
    ]);
    setInput("");
    // fetch job notes structure; populate the fixed quick prompts list
    (async () => {
      try {
        if (job?.jobId) {
          const notes = await getJobNotes(job.jobId);
          setJobNotes(notes);
          setSuggestions(QUICK_PROMPTS);
        }
      } catch (err) {
        // non-blocking
        console.warn("Failed to load job notes", err);
      }
    })();
  }, [open, job]);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Manage render lifecycle so we can animate close before unmount
  useEffect(() => {
    if (showQuickPrompts) {
      if (panelCloseTimer.current) {
        clearTimeout(panelCloseTimer.current);
        panelCloseTimer.current = null;
      }
      setRenderQuickPrompts(true);
      // scroll into view when opened
      if (bodyRef.current) {
        bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
      }
      return;
    }

    // when hiding, wait for animation to finish then unmount
    if (renderQuickPrompts) {
      panelCloseTimer.current = setTimeout(() => {
        setRenderQuickPrompts(false);
        panelCloseTimer.current = null;
      }, 260); // matches CSS transition duration
    }
    return () => {
      if (panelCloseTimer.current) {
        clearTimeout(panelCloseTimer.current);
        panelCloseTimer.current = null;
      }
    };
  }, [showQuickPrompts, renderQuickPrompts]);

  useEffect(() => {
    return () => {
      if (panelCloseTimer.current) clearTimeout(panelCloseTimer.current);
    };
  }, []);

  const formatAiResponse = (text) => {
    if (!text) return "";
    const processInline = (s) => s.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

    const lines = text.split(/\r?\n/);
    const parts = [];
    let currentList = [];

    const flushList = () => {
      if (currentList.length) {
        parts.push(`<ul>${currentList.join("")}</ul>`);
        currentList = [];
      }
    };

    for (let rawLine of lines) {
      const line = rawLine.replace(/\t/g, " ").trimEnd();
      if (line.trim().startsWith("- ")) {
        const item = line.trim().substring(2);
        currentList.push(`<li>${processInline(item)}</li>`);
        continue;
      }

      flushList();

      if (line.trim() === "") {
        parts.push("<br/>");
        continue;
      }

      const containsHtml = /<[^>]+>/.test(line);
      if (containsHtml) {
        parts.push(processInline(line));
      } else {
        parts.push(`<p>${processInline(line)}</p>`);
      }
    }

    flushList();

    return parts.join("\n");
  };

  const handleSend = async (contentParam) => {
    const content = (typeof contentParam === 'string' ? contentParam : input).trim();
    if (!content || !job) return;
    setLastFailedInput(null);
    const userMessage = { id: Date.now(), role: "user", content };
    setMessages((m) => [...m, userMessage]);
    setLoading(true);
    try {
      // include brief hint that jobNotes are available
      const hint = jobNotes ? `\n\n[JOB_NOTES_AVAILABLE]=true` : "";
      const res = await summarizeJob(userId, job.jobId, content + hint, 1000);
      const assistantMsg = { id: `a-${Date.now()}`, role: "assistant", content: res };
      setMessages((m) => [...m, assistantMsg]);
    } catch (err) {
      console.error('AI API error', err);
      // friendly error message for users; store failed input so they can retry
      setLastFailedInput(content);
      const friendly = err?.message && err.message.includes('SqlNullValueException')
        ? 'AI API error: The server returned an internal error while processing notes. Please try again or contact support.'
        : `AI API error: ${err?.message || 'Unexpected error'}`;
      const assistantMsg = { id: `a-${Date.now()}`, role: "assistant", content: friendly };
      setMessages((m) => [...m, assistantMsg]);
    } finally {
      setInput("");
      setLoading(false);
      // hide quick prompts after any send attempt
      setShowQuickPrompts(false);
    }
  };
 const generatePDF = async () => {
  setPdfGenerating(true);
  
  try {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 20;
    const maxContentWidth = pageWidth - (2 * margin);
    
    const chatContainer = document.createElement('div');
    chatContainer.style.cssText = `
      width: ${maxContentWidth}mm;
      background: white;
      padding: 10px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
    `;
    
    const header = document.createElement('div');
    header.style.cssText = `
      text-align: center;
      margin-bottom: 20px;
      padding-bottom: 10px;
      border-bottom: 2px solid #ddd;
    `;
    header.innerHTML = `
      <h1 style="margin: 0; color: #333;">AI Chat - ${job?.jobName || 'Conversation'}</h1>
      <p style="margin: 5px 0 0 0; color: #666; font-size: 12px;">
        Generated: ${new Date().toLocaleString()}
      </p>
    `;
    chatContainer.appendChild(header);
    
    const messagesContainer = document.querySelector('.ai-messages');
    if (messagesContainer) {
      const messagesClone = messagesContainer.cloneNode(true);
      
      const loadingElements = messagesClone.querySelectorAll('.ai-spinner, .loading');
      loadingElements.forEach(el => el.remove());
      
      const allMessages = messagesClone.querySelectorAll('.ai-msg');
      allMessages.forEach(msg => {
        msg.style.cssText = `
          margin-bottom: 15px;
          padding: 10px;
          border-radius: 10px;
          max-width: 90%;
          word-wrap: break-word;
        `;
        
        if (msg.classList.contains('user')) {
          msg.style.backgroundColor = '#dcf8c5';
          msg.style.marginLeft = 'auto';
          msg.style.textAlign = 'right';
        } else {
          msg.style.backgroundColor = '#f1f0f0';
          msg.style.marginRight = 'auto';
          msg.style.textAlign = 'left';
        }
      });
      
      chatContainer.appendChild(messagesClone);
    }
    
    document.body.appendChild(chatContainer);
    
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const canvas = await html2canvas(chatContainer, {
      scale: 2.5,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      windowWidth: chatContainer.scrollWidth,
      windowHeight: chatContainer.scrollHeight
    });
    
    document.body.removeChild(chatContainer);
    
    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const imgWidth = maxContentWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    let heightLeft = imgHeight;
    let position = 0;
    let pageNum = 1;
    const totalPages = Math.ceil(imgHeight / (pageHeight - margin * 2));
    
    while (heightLeft > 0) {
      if (pageNum > 1) {
        pdf.addPage();
      }
      
      pdf.addImage(imgData, 'JPEG', margin, margin - position, imgWidth, imgHeight);
      
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(100, 100, 100);
      const pageText = `Page ${pageNum} of ${totalPages}`;
      const textWidth = pdf.getTextWidth(pageText);
      pdf.text(pageText, (pageWidth - textWidth) / 2, pageHeight - 10);
      
      heightLeft -= (pageHeight - margin * 2);
      position += (pageHeight - margin * 2);
      pageNum++;
    }
    
    pdf.save(`ai_chat_${(job?.jobName || 'conversation').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`);
    toast.success('Chat PDF generated successfully!');
    
  } catch (error) {
    console.error('Error generating PDF:', error);
    toast.error('Failed to generate PDF');
  } finally {
    setPdfGenerating(false);
    setShowPrintDialog(false);
  }
};

  const handleRetry = async () => {
    if (!lastFailedInput) return;
    // set input then call send after state updates
    setInput(lastFailedInput);
    setTimeout(() => {
      handleSend();
    }, 0);
  };

  if (!open) return null;

  return (
    <div className="ai-modal-overlay" onMouseDown={onClose}>
      <div className="ai-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="ai-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className="ai-modal-title">AI Assistant</div>
            <div className="ai-job-meta">{job?.jobName || 'Job'}</div>
          </div>
          <div className="ai-header-actions">
            <button onClick={() => setShowPrintDialog(true)}>🖨️</button>
            <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }} aria-label="close">✕</button>
          </div>
        </div>

        <div className="ai-modal-body" ref={bodyRef}>
          <div className="ai-body-grid">
            <div className="ai-chat-column">
              {/* prompt tabs moved into the Quick Prompts panel below */}
              <div className="ai-messages">
                {messages.map((m) => (
                  <div
                    key={m.id}
                    className={`ai-msg ${m.role === "user" ? "user" : m.role === "assistant" ? "assistant" : "assistant"}`}
                    dangerouslySetInnerHTML={{ __html: m.role === 'assistant' ? formatAiResponse(m.content) : (m.content) }}
                  />
                ))}
                {loading && (
                  <div className="ai-msg assistant">
                    <span className="ai-spinner">⏳</span> Generating response...
                  </div>
                )}
              </div>

              {/* bottom quick prompts panel - toggled from footer */}
              {renderQuickPrompts && (
                <div className={`bottom-prompts-panel ${showQuickPrompts ? 'open' : 'closing'}`}>
                  <div className="bottom-prompts-header">
                    <div>Quick Prompts</div>
                    <button className="close-prompts" onClick={() => setShowQuickPrompts(false)}>✕</button>
                  </div>
                  <div className="bottom-prompts-content">
                    <div className="bottom-prompts-list">
                      {suggestions.map((s, i) => (
                        <button
                          key={i}
                          className="bottom-prompt-item"
                          onClick={() => {
                            // only set input; do NOT send automatically
                            // keep Quick Prompts open until the user explicitly sends
                            setInput(String(s));
                          }}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}


              <div className="ai-modal-footer">
                <button
                  className="toggle-prompts"
                  onClick={() => setShowQuickPrompts((v) => {
                    const newV = !v;
                    if (newV && suggestions.length === 0) {
                      setSuggestions(QUICK_PROMPTS);
                    }
                    return newV;
                  })}
                  aria-pressed={showQuickPrompts}
                >
                  Quick Prompts
                </button>
                <input
                  className="ai-input"
                  placeholder={`Ask about ${job?.jobName || 'this job'}...`}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                />
                  {lastFailedInput && (
                    <button className="ai-retry-btn" onClick={handleRetry} disabled={loading} title="Retry last request">Retry</button>
                  )}
                  <button className="ai-send-btn" onClick={handleSend} disabled={loading || !input.trim()}>
                    {loading ? 'Sending...' : 'Send'}
                  </button>
              </div>
            </div>

            {/* right column removed; Predefined Actions and Notes snapshot moved to bottom prompts panel */}
          </div>
        </div>
          {showPrintDialog && (
          <div className="print-dialog-overlay" onClick={() => setShowPrintDialog(false)}>
            <div className="print-dialog" onClick={(e) => e.stopPropagation()}>
              <h3>Print AI Chat to PDF</h3>
              {pdfGenerating ? (
                <div className="pdf-generating">
                  <i className="fas fa-spinner fa-spin" /> Generating PDF...
                </div>
              ) : (
                <>
                  <div className="print-options">
                    <p>Print the entire AI conversation?</p>
                    <p><small>{messages.filter(m => m.role !== 'system').length} messages will be printed</small></p>
                  </div>
                  <div className="buttons">
                    <button className="generate-btn" onClick={generatePDF}>
                      <i className="fas fa-file-pdf" /> Generate PDF
                    </button>
                    <button className="cancel-btn" onClick={() => setShowPrintDialog(false)}>
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

AiChatDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  job: PropTypes.object,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

AiChatDialog.defaultProps = {
  open: false,
  onClose: () => {},
  job: null,
  userId: null,
};

export default AiChatDialog;
